import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import entete from "../../../assets/img/entete.jpg";
import { frenchDateOnly } from "../../../variables/functions";
import { displayNumbers } from "../../../variables/numbersFunctions";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";

const border = "1px solid #333";
const th = { border, padding: "6px 8px", fontSize: 12, background: "#dbe5f1", textAlign: "center" };
const td = { border, padding: "5px 8px", fontSize: 12 };
const tdR = { ...td, textAlign: "right", whiteSpace: "nowrap" };

// Net reversé = montant total des primes − commissions retenues (le mapper n'expose
// que montantTotal et montantCommissions ; cohérent avec le bordereau unitaire).
const montantsOf = (b) => {
    const total = Number(b?.montantTotal) || 0;
    const commissions = Number(b?.montantCommissions) || 0;
    return { total, commissions, net: total - commissions };
};

const modePaiementLabel = (b) => {
    const m = b?.modePaiement;
    const num = b?.numeroPaiement;
    const banque = b?.banque;
    if (!m || m === "-") return "—";
    switch (m) {
        case "ESPECES":
        case "ESPECE":
            return "Espèces";
        case "BANQUE":
            return `Chèque${num ? ` n° ${num}` : ""}${banque ? ` — ${banque}` : ""}`;
        case "VIREMENT":
            return `Virement${num ? ` — réf. ${num}` : ""}${banque ? ` — ${banque}` : ""}`;
        default:
            return m;
    }
};

// Date de versement avec repli (même logique que le backend) : dateReglement du bordereau,
// sinon la dateVersementCompagnie la plus récente des règlements, sinon date de modification/création.
// Évite un « — » sur les bordereaux réglés dont la dateReglement n'a pas été renseignée.
const versementDateOf = (b) => {
    if (b?.dateReglement) return b.dateReglement;
    const versDates = (b?.reglements || [])
        .map((r) => r?.dateVersementCompagnie)
        .filter(Boolean)
        .sort();
    if (versDates.length) return versDates[versDates.length - 1];
    return b?.updatedAt || b?.dateModification || b?.createdAt || b?.dateCreation || null;
};

// Compagnie (organisme) d'un bordereau : déduite des règlements (contrat.organismeCode),
// avec repli sur l'organisme de l'agence.
const orgCodeOf = (b) => {
    const r = (b?.reglements || []).find((x) => x?.contrat?.organismeCode);
    return r?.contrat?.organismeCode || b?.agence?.organisme?.code || null;
};

// Groupe les bordereaux par agence en conservant l'ordre (déjà trié par agence côté backend).
const groupByAgence = (bordereaux) => {
    const groups = new Map();
    bordereaux.forEach((b) => {
        const key = b?.agence?.codeId || b?.agence?.codeAgence || b?.agence?.nom || "—";
        if (!groups.has(key)) groups.set(key, { agence: b?.agence, items: [] });
        groups.get(key).items.push(b);
    });
    return Array.from(groups.values());
};

const pageStyle = {
    width: "210mm",
    minHeight: "297mm",
    padding: "12mm",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
};

/**
 * État des bordereaux de reversement VERSÉS en compagnie, groupé par agence —
 * une agence par page. Reçoit la liste déjà filtrée (versés ∩ période ∩ agence) et triée
 * par agence côté backend.
 */
const EtatBordereauxReversement = React.forwardRef((props, ref) => {
    const bordereaux = props.bordereaux || [];
    const periode = props.periode || "";
    const logos = props.logos || {};
    const groups = groupByAgence(bordereaux);

    return (
        <div ref={ref}>
            {groups.length === 0 && (
                <div style={pageStyle}>
                    <img src={entete} alt="" style={{ width: "100%", maxHeight: 110, objectFit: "contain" }} />
                    <p style={{ marginTop: 24 }}>Aucun bordereau versé sur la période sélectionnée.</p>
                </div>
            )}
            {groups.map((group, gi) => {
                const totaux = group.items.reduce(
                    (acc, b) => {
                        const m = montantsOf(b);
                        acc.total += m.total;
                        acc.commissions += m.commissions;
                        acc.net += m.net;
                        return acc;
                    },
                    { total: 0, commissions: 0, net: 0 }
                );
                const logoCompagnie = logos[orgCodeOf(group.items[0])];
                return (
                    <div
                        key={gi}
                        style={{ ...pageStyle, pageBreakAfter: gi < groups.length - 1 ? "always" : "auto" }}
                    >
                        <div style={{ flexShrink: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                                {logoCompagnie ? (
                                    <img src={logoCompagnie} alt="" style={{ maxWidth: "42%", maxHeight: 90, objectFit: "contain" }} />
                                ) : (
                                    <span />
                                )}
                                <img src={entete} alt="" style={{ maxWidth: logoCompagnie ? "54%" : "100%", maxHeight: 90, objectFit: "contain" }} />
                            </div>
                            <h3 style={{ textAlign: "center", color: "#272366", margin: "8px 0 2px" }}>
                                ÉTAT DES BORDEREAUX DE REVERSEMENT VERSÉS
                            </h3>
                            <div style={{ textAlign: "center", fontSize: 13, marginBottom: 2 }}>
                                Point de vente : <b>{group.agence?.nom || "—"}</b>
                            </div>
                            {periode && (
                                <div style={{ textAlign: "center", fontSize: 12, marginBottom: 8 }}>{periode}</div>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, width: 36 }}>N°</th>
                                        <th style={th}>N° Bordereau</th>
                                        <th style={th}>Date versement</th>
                                        <th style={th}>Montant total</th>
                                        <th style={th}>Commissions</th>
                                        <th style={th}>Net reversé</th>
                                        <th style={th}>Mode / Réf paiement</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((b, i) => {
                                        const m = montantsOf(b);
                                        return (
                                            <tr key={b.codeId || i}>
                                                <td style={{ ...td, textAlign: "center" }}>{i + 1}</td>
                                                <td style={td}>{b.numeroBordereau || "—"}</td>
                                                <td style={{ ...td, textAlign: "center" }}>
                                                    {versementDateOf(b) ? frenchDateOnly(versementDateOf(b)) : "—"}
                                                </td>
                                                <td style={tdR}>{displayNumbers(m.total)}</td>
                                                <td style={tdR}>{displayNumbers(m.commissions)}</td>
                                                <td style={tdR}>{displayNumbers(m.net)}</td>
                                                <td style={td}>{modePaiementLabel(b)}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr style={{ fontWeight: 700, background: "#e2efda" }}>
                                        <td style={{ ...td, textAlign: "center" }} colSpan={3}>
                                            TOTAUX ({group.items.length})
                                        </td>
                                        <td style={tdR}>{displayNumbers(totaux.total)}</td>
                                        <td style={tdR}>{displayNumbers(totaux.commissions)}</td>
                                        <td style={tdR}>{displayNumbers(totaux.net)}</td>
                                        <td style={td}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ flexShrink: 0, marginTop: "6mm" }}>
                            {/* Date et heure d'impression, même format et emplacement que sur les
                            Conditions Particulières (pièce comptable → l'exemplaire papier doit
                            porter son horodatage). */}
                            <p style={{ marginLeft: "15px" }}>
                            Imprimé le : {dayjs().format("DD/MM/YYYY HH:mm:ss")}
                            </p>
                            <DocumentFooter />
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

EtatBordereauxReversement.propTypes = {
    bordereaux: PropTypes.array,
    periode: PropTypes.string,
    logos: PropTypes.object, // { organismeCode: objectURL } — logo compagnie par page/agence
};

export default EtatBordereauxReversement;
